import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchData from '@shared/hooks/useFetchData';
import { generateDossierPreviewPdf } from '@utils/pdf';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useOrgUnitSearch } from '../../hooks/useOrgUnitSearch';
import { personalFilesService, PERSONAL_FILES_SEARCH_OPTIONS } from '../services/personalFilesService';

const DEFAULT_SORT_BY = 'empId';
const DEFAULT_SORT_DIR = 'asc';
const PERSONAL_FILES_FETCH_OPTIONS = { blockingError: true };

export function usePersonalFiles() {
    const { t } = useTranslation();
    const [pdfLoading, setPdfLoading] = useState(false);
    const {
        data,
        loading,
        error,
        pageNumber,
        pageSize,
        sortBy,
        sortDir,
        totalElements,
        totalPages,
        searchQuery,
        searchType,
        setSearchQuery,
        setSearchType,
        setPageSize,
        setpageNumber,
        fetchData,
        handleSearchClick,
        handleSort,
    } = useFetchData();

    const {
        showOrgUnitPicker,
        selectedOrgUnitLabel,
        openOrgUnitPicker,
        handleOrgUnitPickerCancel,
        handleOrgUnitSelect,
    } = useOrgUnitSearch({
        searchQuery,
        setSearchQuery,
        setpageNumber,
        onApplySearch: (nextSearchQuery) => {
            fetchData(personalFilesService.getRegisterList, {
                pageNumber: 0,
                searchQuery: nextSearchQuery,
                sortBy: sortBy || DEFAULT_SORT_BY,
                sortDir: sortDir || DEFAULT_SORT_DIR,
            }, PERSONAL_FILES_FETCH_OPTIONS);
        },
    });

    const columns = useMemo(
        () => [
            { key: 'number', label: t('staff.reports.personalFiles.table.headers.number') },
            { key: 'name', label: t('staff.reports.personalFiles.table.headers.name') },
            { key: 'idn', label: t('staff.reports.personalFiles.table.headers.idn') },
        ],
        [t]
    );

    const searchOptions = useMemo(() => PERSONAL_FILES_SEARCH_OPTIONS, []);

    const loadRegister = useCallback(
        (overrides = {}) =>
            fetchData(personalFilesService.getRegisterList, {
                searchQuery,
                sortBy: sortBy || DEFAULT_SORT_BY,
                sortDir: sortDir || DEFAULT_SORT_DIR,
                ...overrides,
            }, PERSONAL_FILES_FETCH_OPTIONS),
        [fetchData, searchQuery, sortBy, sortDir]
    );

    useEffect(() => {
        loadRegister();
    }, [pageNumber, pageSize, sortBy, sortDir]);

    const handleTableSearchClick = useCallback(() => {
        handleSearchClick(personalFilesService.getRegisterList, {
            searchQuery,
            sortBy: sortBy || DEFAULT_SORT_BY,
            sortDir: sortDir || DEFAULT_SORT_DIR,
        }, PERSONAL_FILES_FETCH_OPTIONS);
    }, [handleSearchClick, searchQuery, sortBy, sortDir]);

    const handleTableSort = useCallback(
        (columnKey) => {
            handleSort(columnKey, personalFilesService.getRegisterList);
        },
        [handleSort]
    );

    const handleRowClick = useCallback(
        async (index) => {
            const row = data?.[index];
            const empFileId = row?.id;
            if (empFileId == null || empFileId === '') {
                return;
            }

            try {
                setPdfLoading(true);
                const dossierData = await personalFilesService.getDossierData(empFileId);
                await generateDossierPreviewPdf({ dossierData });
            } catch (err) {
                console.error('Error generating personal file PDF:', err);
                const message =
                    err?.response?.data?.message
                    ?? (typeof err?.response?.data === 'string' ? err.response.data : null)
                    ?? err?.message
                    ?? t('common.errorMessage');
                notifyError(message);
            } finally {
                setPdfLoading(false);
            }
        },
        [data, t]
    );

    return {
        title: t('staff.reports.personalFiles.title'),
        columns,
        searchOptions,
        data,
        loading: loading || pdfLoading,
        error,
        pageNumber,
        pageSize,
        sortBy,
        sortDir,
        totalElements,
        totalPages,
        searchQuery,
        searchType,
        setSearchQuery,
        setSearchType,
        setPageSize,
        setpageNumber,
        getData: loadRegister,
        handleSearchClick: handleTableSearchClick,
        handleSort: handleTableSort,
        handleRowClick,
        showOrgUnitPicker,
        openOrgUnitPicker,
        handleOrgUnitPickerCancel,
        handleOrgUnitSelect,
        selectedOrgUnitLabel,
    };
}
